import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { EN, LANGUAGES } from './en';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'ewrf.lang';

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
  translations: EN,
  languages: LANGUAGES,
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
    } catch (_) { /* localStorage unavailable */ }
    return 'en';
  });

  const [translations, setTranslations] = useState(EN);
  const [loading, setLoading] = useState(false);

  const setLang = useCallback((code) => {
    if (!LANGUAGES.some((l) => l.code === code)) return;
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* ignore */ }
  }, []);

  // Fetch translations when language changes (except English which is bundled)
  useEffect(() => {
    if (lang === 'en') {
      setTranslations(EN);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/content/translations/${lang}`);
        if (!cancelled) {
          // Merge English fallbacks for any missing keys
          setTranslations({ ...EN, ...(res.data?.strings || {}) });
        }
      } catch (_) {
        if (!cancelled) setTranslations(EN);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const t = useCallback((key) => {
    if (translations[key]) return translations[key];
    return EN[key] || key;
  }, [translations]);

  const value = useMemo(() => ({ lang, setLang, t, translations, languages: LANGUAGES, loading }), [lang, setLang, t, translations, loading]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;

export default I18nProvider;
